import React, { useEffect, useId, useMemo, useState } from "react";
import {
  gfNormalizeEmailFieldValue,
  gfNormalizeNameTextFieldValue,
  gfNormalizePasswordFieldValue,
  gfNormalizeUnsignedNumberValue,
  gfValidateEmailField,
  gfValidateNameTextField,
  gfValidatePasswordField,
  gfValidateUnsignedNumber,
  GF_NAME_TEXT_RULES_MAX_LEN,
  GF_PASSWORD_POLICY_MAX_LEN
} from "./globalFormValidation";
import "./GlobalFormFields.css";

function buildAriaDescribedBy({ baseAlertId, errorId, extraIds }) {
  const ids = [];
  if (errorId) ids.push(errorId);
  if (baseAlertId) ids.push(baseAlertId);
  if (extraIds) {
    const more = String(extraIds)
      .split(" ")
      .map((s) => s.trim())
      .filter(Boolean);
    ids.push(...more);
  }
  return ids.length ? ids.join(" ") : undefined;
}

export function GlobalTextField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  autoComplete,
  required,
  disabled,
  inputMode,
  maxLength,
  minLength,
  min,
  max,
  step,
  type = "text",
  normalizeOnChangeValue,
  /** When true, applies name-style rules (letters, single spaces, one dot, 4–40 letters/length trim, no digits). */
  nameTextRules = false,
  /** When true, trims email, single @, no consecutive dots, validates format (use only one of the *Rules props). */
  emailFormatRules = false,
  /** When true, strips minus and invalid chars; non-negative number validation (use only one of the *Rules props). */
  unsignedNumberRules = false,
  classNames = {},
  baseAlertId,
  describedByIds,
  onBlur,
  onKeyDown,
  inputRef,
  /** When true, no `<label>` is rendered (use an external label with `htmlFor` matching `id`, or `aria-label`). */
  hideLabel = false,
}) {
  const fallbackId = useId();
  const inputId = id || `gff-text-${fallbackId}`;
  const [internalError, setInternalError] = useState("");

  const rulesMode =
    nameTextRules ? "name" : emailFormatRules ? "email" : unsignedNumberRules ? "unsigned" : null;

  const effectiveMaxLength =
    nameTextRules && maxLength == null ? GF_NAME_TEXT_RULES_MAX_LEN : maxLength;

  const effectiveInputMode =
    emailFormatRules && inputMode == null ? "email" : inputMode;

  useEffect(() => {
    if (!rulesMode) {
      setInternalError("");
      return;
    }
    const v = value ?? "";
    if (!String(v).trim()) {
      setInternalError("");
      return;
    }
    if (rulesMode === "name") setInternalError(gfValidateNameTextField(v));
    else if (rulesMode === "email") setInternalError(gfValidateEmailField(v));
    else setInternalError(gfValidateUnsignedNumber(v));
  }, [value, rulesMode]);

  function normalizeForRules(raw) {
    if (unsignedNumberRules) return gfNormalizeUnsignedNumberValue(raw);
    if (emailFormatRules) return gfNormalizeEmailFieldValue(raw);
    if (nameTextRules) return gfNormalizeNameTextFieldValue(raw);
    return raw;
  }

  function validateForRules(next) {
    if (!String(next ?? "").trim()) return "";
    if (nameTextRules) return gfValidateNameTextField(next);
    if (emailFormatRules) return gfValidateEmailField(next);
    if (unsignedNumberRules) return gfValidateUnsignedNumber(next);
    return "";
  }

  const combinedError = error || internalError;
  const errorId = combinedError ? `${inputId}-error` : undefined;
  const ariaDescribedBy = buildAriaDescribedBy({
    baseAlertId,
    errorId,
    extraIds: describedByIds
  });

  return (
    <p className={classNames.fieldWrap || "gff-field"}>
      {!hideLabel ? (
        <label
          className={`${classNames.label || "gff-label"}${
            combinedError ? ` ${classNames.labelError || "gff-label--error"}` : ""
          }`}
          htmlFor={inputId}
        >
          {label}
        </label>
      ) : null}
      <input
        id={inputId}
        className={`${classNames.input || "gff-input"}${
          combinedError ? ` ${classNames.inputError || "gff-input--error"}` : ""
        }`}
        type={type}
        value={value ?? ""}
        onChange={(e) => {
          let next = e.target.value;
          if (rulesMode) {
            next = normalizeForRules(next);
          }
          if (normalizeOnChangeValue) {
            next = normalizeOnChangeValue(next);
          }
          onChange?.(next, e);
          if (rulesMode) {
            setInternalError(validateForRules(next));
          }
        }}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        inputMode={effectiveInputMode}
        maxLength={effectiveMaxLength}
        minLength={minLength}
        min={min}
        max={max}
        step={step}
        aria-invalid={combinedError ? "true" : "false"}
        aria-describedby={ariaDescribedBy}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          if (rulesMode) {
            const raw = value ?? "";
            const t = normalizeForRules(raw);
            if (t !== raw) {
              onChange?.(t, e);
            }
            setInternalError(validateForRules(t));
          }
          onBlur?.(e);
        }}
      />
      {combinedError ? (
        <span
          id={errorId}
          className={classNames.error || "gff-error"}
          role="alert"
        >
          {combinedError}
        </span>
      ) : null}
    </p>
  );
}

export function GlobalEmailField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  autoComplete = "email",
  required,
  disabled,
  classNames,
  baseAlertId,
  describedByIds,
  /** When true, enforces single @, no consecutive dots, and format checks inside the field. */
  emailFormatRules = false
}) {
  return (
    <GlobalTextField
      id={id}
      label={label}
      type={emailFormatRules ? "text" : "email"}
      value={value}
      onChange={onChange}
      error={error}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      disabled={disabled}
      emailFormatRules={emailFormatRules}
      classNames={classNames}
      baseAlertId={baseAlertId}
      describedByIds={describedByIds}
    />
  );
}

export function GlobalContactNumberField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
  disabled,
  classNames,
  baseAlertId,
  describedByIds
}) {
  return (
    <GlobalTextField
      id={id}
      label={label}
      type="text"
      value={value}
      onChange={onChange}
      error={error}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      inputMode="numeric"
      minLength={10}
      maxLength={10}
      normalizeOnChangeValue={(raw) =>
        String(raw).replace(/\D/g, "").slice(0, 10)
      }
      classNames={classNames}
      baseAlertId={baseAlertId}
      describedByIds={describedByIds}
    />
  );
}

export function GlobalSelectField({
  id,
  label,
  value,
  onChange,
  error,
  required,
  disabled,
  options = [],
  placeholderOption = "—",
  classNames = {},
  baseAlertId,
  describedByIds
}) {
  const fallbackId = useId();
  const inputId = id || `gff-select-${fallbackId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const ariaDescribedBy = buildAriaDescribedBy({
    baseAlertId,
    errorId,
    extraIds: describedByIds
  });

  return (
    <p className={classNames.fieldWrap || "gff-field"}>
      <label
        className={`${classNames.label || "gff-label"}${
          error ? ` ${classNames.labelError || "gff-label--error"}` : ""
        }`}
        htmlFor={inputId}
      >
        {label}
      </label>
      <select
        id={inputId}
        className={`${classNames.select || classNames.input || "gff-select"}${
          error ? ` ${classNames.inputError || "gff-input--error"}` : ""
        }`}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value, e)}
        required={required}
        disabled={disabled}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={ariaDescribedBy}
      >
        <option value="">{placeholderOption}</option>
        {options.map((opt, idx) => {
          const optLabel =
            opt.label != null ? String(opt.label) : String(opt.value ?? "");
          return (
            <option
              key={`${idx}-${String(opt.value)}`}
              value={opt.value}
              title={optLabel}
            >
              {optLabel}
            </option>
          );
        })}
      </select>
      {error ? (
        <span
          id={errorId}
          className={classNames.error || "gff-error"}
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </p>
  );
}

export function GlobalInlineSelect({
  id,
  value,
  onChange,
  options = [],
  disabled,
  className = "",
  placeholderOption,
  ariaLabel
}) {
  return (
    <select
      id={id}
      className={`gff-select ${className}`.trim()}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value, e)}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {placeholderOption !== undefined ? (
        <option value="">{placeholderOption}</option>
      ) : null}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function GlobalButton({
  type = "button",
  variant = "ghost",
  size,
  disabled,
  loading = false,
  onClick,
  children,
  className = "",
  ariaLabel,
  style,
  ...rest
}) {
  const variantClass =
    variant === "primary"
      ? "gff-btn--primary"
      : variant === "danger"
        ? "gff-btn--danger"
        : "gff-btn--ghost";
  const sizeClass = size === "sm" ? " gff-btn--sm" : "";

  return (
    <button
      type={type}
      className={`gff-btn${sizeClass} ${variantClass} ${className}`.trim()}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-busy={loading ? "true" : "false"}
      style={style}
      {...rest}
    >
      {loading ? "Loading..." : children}
    </button>
  );
}

export function GlobalTextAreaField({
  id,
  label,
  value,
  onChange,
  error,
  required,
  disabled,
  rows = 2,
  maxLength,
  classNames = {},
  baseAlertId,
  describedByIds
}) {
  const fallbackId = useId();
  const inputId = id || `gff-textarea-${fallbackId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const ariaDescribedBy = buildAriaDescribedBy({
    baseAlertId,
    errorId,
    extraIds: describedByIds
  });

  return (
    <p className={classNames.fieldWrap || "gff-field"}>
      <label
        className={`${classNames.label || "gff-label"}${
          error ? ` ${classNames.labelError || "gff-label--error"}` : ""
        }`}
        htmlFor={inputId}
      >
        {label}
      </label>
      <textarea
        id={inputId}
        className={`${classNames.textarea || "gff-textarea"}${
          error ? ` ${classNames.textareaError || "gff-textarea--error"}` : ""
        }`}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value, e)}
        required={required}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={ariaDescribedBy}
      />
      {error ? (
        <span
          id={errorId}
          className={classNames.error || "gff-error"}
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </p>
  );
}

export function GlobalDateField({
  id,
  label,
  value,
  onChange,
  error,
  required,
  disabled,
  min,
  max,
  classNames = {},
  baseAlertId,
  describedByIds
}) {
  const fallbackId = useId();
  const inputId = id || `gff-date-${fallbackId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const ariaDescribedBy = buildAriaDescribedBy({
    baseAlertId,
    errorId,
    extraIds: describedByIds
  });

  return (
    <p className={classNames.fieldWrap || "gff-field"}>
      <label
        className={`${classNames.label || "gff-label"}${
          error ? ` ${classNames.labelError || "gff-label--error"}` : ""
        }`}
        htmlFor={inputId}
      >
        {label}
      </label>
      <input
        id={inputId}
        className={`${classNames.input || "gff-input"}${
          error ? ` ${classNames.inputError || "gff-input--error"}` : ""
        }`}
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value, e)}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={ariaDescribedBy}
      />
      {error ? (
        <span
          id={errorId}
          className={classNames.error || "gff-error"}
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </p>
  );
}

export function GlobalPasswordField({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  autoComplete = "new-password",
  required,
  disabled,
  classNames = {},
  baseAlertId,
  describedByIds,
  defaultVisible = false,
  visibleLabel = { show: "Show password", hide: "Hide password" },
  /** Built-in show/hide control (eye). Default true. */
  showPasswordToggle = true,
  /**
   * When true, enforces 8–15 chars, one uppercase, one digit, one non-space special character,
   * and caps length while typing.
   */
  passwordPolicyRules = false
}) {
  const fallbackId = useId();
  const inputId = id || `gff-password-${fallbackId}`;
  const [visible, setVisible] = useState(Boolean(defaultVisible));
  const [internalError, setInternalError] = useState("");

  useEffect(() => {
    if (!passwordPolicyRules) {
      setInternalError("");
      return;
    }
    const p = value ?? "";
    if (!String(p).length) {
      setInternalError("");
      return;
    }
    setInternalError(gfValidatePasswordField(p));
  }, [value, passwordPolicyRules]);

  const combinedError = error || internalError;
  const errorId = combinedError ? `${inputId}-error` : undefined;
  const ariaDescribedBy = useMemo(
    () =>
      buildAriaDescribedBy({
        baseAlertId,
        errorId,
        extraIds: describedByIds
      }),
    [baseAlertId, errorId, describedByIds]
  );

  const inputEl = (
    <input
      id={inputId}
      className={`${classNames.input || "gff-input"}${
        combinedError ? ` ${classNames.inputError || "gff-input--error"}` : ""
      }`}
      type={visible ? "text" : "password"}
      value={value ?? ""}
      onChange={(e) => {
        let next = e.target.value;
        if (passwordPolicyRules) {
          next = gfNormalizePasswordFieldValue(next);
        }
        onChange?.(next, e);
        if (passwordPolicyRules) {
          setInternalError(
            !String(next).length ? "" : gfValidatePasswordField(next)
          );
        }
      }}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={required}
      disabled={disabled}
      maxLength={passwordPolicyRules ? GF_PASSWORD_POLICY_MAX_LEN : undefined}
      aria-invalid={combinedError ? "true" : "false"}
      aria-describedby={ariaDescribedBy}
      onBlur={(e) => {
        if (passwordPolicyRules) {
          const raw = value ?? "";
          const t = gfNormalizePasswordFieldValue(raw);
          if (t !== raw) {
            onChange?.(t, e);
          }
          setInternalError(!t ? "" : gfValidatePasswordField(t));
        }
      }}
    />
  );

  return (
    <p className={classNames.fieldWrap || "gff-field"}>
      <label
        className={`${classNames.label || "gff-label"}${
          combinedError ? ` ${classNames.labelError || "gff-label--error"}` : ""
        }`}
        htmlFor={inputId}
      >
        {label}
      </label>
      {showPasswordToggle ? (
        <div className={classNames.passwordWrap || "gff-password-wrap"}>
          {inputEl}
          <button
            type="button"
            className={classNames.passwordToggle || "gff-password-toggle"}
            onClick={() => setVisible((p) => !p)}
            disabled={disabled}
            aria-label={visible ? visibleLabel.hide : visibleLabel.show}
          >
            {visible ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      ) : (
        inputEl
      )}
      {combinedError ? (
        <span
          id={errorId}
          className={classNames.error || "gff-error"}
          role="alert"
        >
          {combinedError}
        </span>
      ) : null}
    </p>
  );
}

